import { onlineManager } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { type AssistantAction, type ChatMessage, type ChatPhoto, streamChat } from "@/api/assistant";
import { useInvalidateWorkspace } from "@/api/finance";
import { Button } from "@/components/ui/button";
import { useWorkspace } from "@/layout/workspace-context";
import { currentBreakpoint } from "@/lib/breakpoint";
import { isNetworkError } from "@/pwa/online";

// O painel (e o cartão) só carregam quando abertos: fora do JS inicial.
const AssistantPanel = lazy(() => import("./AssistantPanel"));

export type ChatItem =
  /** `via: "voice"`: transcrição da chamada de voz (só na tela; não vai no histórico do texto, F3-D6). */
  | { id: number; kind: "user" | "assistant"; text: string; via?: "voice" }
  /** `receipt`: o arquivo original da foto que gerou a proposta, só em memória, para anexar ao confirmar (#37). */
  | { id: number; kind: "proposal"; action: AssistantAction; confirmedVia?: "voice"; receipt?: File }
  | { id: number; kind: "error"; text: string };

/** Pedaço da transcrição de voz: o mesmo `id` (item da OpenAI) atualiza o mesmo balão. */
export interface VoiceTranscriptPiece {
  id: string;
  role: "user" | "assistant";
  text: string;
}

interface AssistantState {
  items: ChatItem[];
  sending: boolean;
  /** `receipt`: o arquivo original da foto (a `photo` é a cópia reduzida para o assistente). */
  send(text: string, photo?: ChatPhoto, receipt?: File): Promise<void>;
  reset(): void;
  updateAction(action: AssistantAction): void;
  /** Voz (fase 3): a chamada em si vive no `VoiceControls` (carregado sob demanda); a conversa, aqui. */
  voice: {
    transcript(piece: VoiceTranscriptPiece): void;
    proposal(action: AssistantAction): void;
    /** Confirmação falada (ou outro estado vindo da voz): atualiza o cartão e recarrega os dados. */
    actionUpdated(action: AssistantAction): void;
  };
}

const OpenContext = createContext<(() => void) | null>(null);
const StateContext = createContext<AssistantState | null>(null);

export function useAssistant() {
  const state = useContext(StateContext);
  if (!state) throw new Error("useAssistant fora do AssistantProvider");
  return state;
}

/** Entrada do "+" no assistente: abre o painel já com uma foto. */
export interface AssistantEntry {
  openWithPhoto(file: File, opener: HTMLElement | null): void;
}
const EntryContext = createContext<AssistantEntry | null>(null);

/** `null` quando o assistente não está liberado para o usuário (o "+" segue direto para o formulário). */
export function useAssistantEntry() {
  return useContext(EntryContext);
}

/** Foto vinda de fora do painel, à espera do composer. `take` a entrega uma vez só (à prova do StrictMode). */
interface PendingPhoto {
  id: number;
  take(): File | null;
}
const PendingPhotoContext = createContext<PendingPhoto | null>(null);

export function usePendingPhoto() {
  return useContext(PendingPhotoContext);
}

const INTERRUPTED = "A resposta foi interrompida. Tente de novo.";
const NEEDS_NETWORK = "O assistente precisa de conexão com a internet.";

/** O que fica da foto no histórico: só este texto (a imagem não é guardada nem reenviada). */
export const PHOTO_SENT = "📷 foto enviada";

/** Limites do servidor: o histórico mais antigo sai antes de enviar. */
const MAX_MESSAGES = 40;
const MAX_MESSAGE_CHARS = 4000;
const MAX_TOTAL_CHARS = 30_000;

export function historyFor(items: ChatItem[]): ChatMessage[] {
  const messages: ChatMessage[] = items.flatMap((i) =>
    (i.kind === "user" || i.kind === "assistant") && !i.via && i.text.trim()
      ? [{ role: i.kind, content: i.text.trim().slice(0, MAX_MESSAGE_CHARS) }]
      : [],
  );
  let total = messages.reduce((n, m) => n + m.content.length, 0);
  while (messages.length > MAX_MESSAGES || total > MAX_TOTAL_CHARS) {
    total -= messages.shift()?.content.length ?? 0;
  }
  while (messages[0]?.role === "assistant") messages.shift();
  return messages;
}

/**
 * Conversa do assistente, só em memória: trocar de tela mantém; recarregar, fechar a aba, trocar de
 * workspace (o provider é remontado) ou "Nova conversa" apagam. Nada vai para o armazenamento do navegador.
 * O arquivo original de uma foto fica preso ao cartão que ela gerou e some com ele.
 */
export function AssistantProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const ws = useWorkspace();
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [items, setItems] = useState<ChatItem[]>([]);
  const [sending, setSending] = useState(false);
  const nextId = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  /** Item da transcrição (OpenAI) → balão na conversa. */
  const voiceItems = useRef(new Map<string, number>());
  const invalidate = useInvalidateWorkspace(ws.id);

  // Trocar de workspace remonta o provider: a resposta em curso é abortada (não gasta mais tokens).
  useEffect(() => () => abort.current?.abort(), []);

  const openPanel = useCallback(() => {
    opener.current = document.activeElement as HTMLElement | null;
    setMobile(currentBreakpoint() === "mobile");
    setOpen(true);
  }, []);

  // O "+" passa quem abriu: depois do seletor de arquivos, o foco já não está nele.
  const [pendingId, setPendingId] = useState(0);
  const pendingFile = useRef<File | null>(null);
  const openWithPhoto = useCallback((file: File, from: HTMLElement | null) => {
    pendingFile.current = file;
    setPendingId((n) => n + 1);
    opener.current = from;
    setMobile(currentBreakpoint() === "mobile");
    setOpen(true);
  }, []);
  const entry = useMemo(() => (enabled ? { openWithPhoto } : null), [enabled, openWithPhoto]);
  const pending = useMemo<PendingPhoto>(
    () => ({
      id: pendingId,
      take: () => {
        const file = pendingFile.current;
        pendingFile.current = null;
        return file;
      },
    }),
    [pendingId],
  );

  const send = useCallback(
    async (text: string, photo?: ChatPhoto, receipt?: File) => {
      const caption = text.trim();
      const content = photo ? (caption ? `${PHOTO_SENT}\n${caption}` : PHOTO_SENT) : caption;
      if (!content || sending) return;
      const id = () => ++nextId.current;
      const userItem: ChatItem = { id: id(), kind: "user", text: content };
      const history = historyFor([...items, userItem]);
      let current: number | null = null;
      let finished = false;
      setItems((prev) => [...prev, userItem]);
      // Sem rede: nem tenta (a conversa da aba fica como está).
      if (!onlineManager.isOnline()) {
        const item: ChatItem = { id: id(), kind: "error", text: NEEDS_NETWORK };
        setItems((prev) => [...prev, item]);
        return;
      }
      setSending(true);
      const controller = new AbortController();
      abort.current = controller;
      try {
        await streamChat(
          ws.id,
          history,
          (e) => {
            if (e.type === "delta") {
              // Os updaters ficam puros (o StrictMode pode chamá-los duas vezes): o id é decidido fora.
              if (current === null) {
                const created = id();
                current = created;
                setItems((prev) => [...prev, { id: created, kind: "assistant", text: e.text }]);
              } else {
                const target = current;
                setItems((prev) =>
                  prev.map((i) =>
                    i.id === target && i.kind === "assistant" ? { ...i, text: i.text + e.text } : i,
                  ),
                );
              }
            } else if (e.type === "proposal") {
              current = null;
              const item: ChatItem = {
                id: id(),
                kind: "proposal",
                action: e.action,
                ...(photo && receipt ? { receipt } : {}),
              };
              setItems((prev) => [...prev, item]);
            } else if (e.type === "error") {
              finished = true;
              const item: ChatItem = { id: id(), kind: "error", text: e.message };
              setItems((prev) => [...prev, item]);
            } else if (e.type === "done") {
              finished = true;
            }
          },
          controller.signal,
          photo,
        );
        if (!finished && !controller.signal.aborted) {
          const item: ChatItem = { id: id(), kind: "error", text: INTERRUPTED };
          setItems((prev) => [...prev, item]);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          const item: ChatItem = {
            id: id(),
            kind: "error",
            text: isNetworkError(err)
              ? NEEDS_NETWORK
              : err instanceof Error
                ? err.message
                : "Algo deu errado.",
          };
          setItems((prev) => [...prev, item]);
        }
      } finally {
        if (abort.current === controller) abort.current = null;
        setSending(false);
      }
    },
    [items, sending, ws.id],
  );

  const reset = useCallback(() => {
    abort.current?.abort();
    voiceItems.current.clear();
    setItems([]);
    setSending(false);
  }, []);

  const updateAction = useCallback((action: AssistantAction) => {
    setItems((prev) =>
      prev.map((i) => (i.kind === "proposal" && i.action.id === action.id ? { ...i, action } : i)),
    );
  }, []);

  const voice = useMemo(
    () => ({
      transcript(piece: VoiceTranscriptPiece) {
        const existing = voiceItems.current.get(piece.id);
        if (existing !== undefined) {
          setItems((prev) =>
            prev.map((i) => (i.id === existing && i.kind === piece.role ? { ...i, text: piece.text } : i)),
          );
          return;
        }
        const created = ++nextId.current;
        voiceItems.current.set(piece.id, created);
        setItems((prev) => [...prev, { id: created, kind: piece.role, text: piece.text, via: "voice" }]);
      },
      proposal(action: AssistantAction) {
        const item: ChatItem = { id: ++nextId.current, kind: "proposal", action };
        setItems((prev) => [...prev, item]);
      },
      actionUpdated(action: AssistantAction) {
        setItems((prev) =>
          prev.map((i) =>
            i.kind === "proposal" && i.action.id === action.id
              ? { ...i, action, ...(action.status === "confirmed" ? { confirmedVia: "voice" as const } : {}) }
              : i,
          ),
        );
        if (action.status === "confirmed") void invalidate();
      },
    }),
    [invalidate],
  );

  const state = useMemo(
    () => ({ items, sending, send, reset, updateAction, voice }),
    [items, sending, send, reset, updateAction, voice],
  );

  return (
    <OpenContext.Provider value={enabled ? openPanel : null}>
      <EntryContext.Provider value={entry}>
        <StateContext.Provider value={state}>
          {children}
          {enabled && open && (
            <Suspense fallback={null}>
              <PendingPhotoContext.Provider value={pending}>
                <AssistantPanel
                  mobile={mobile}
                  onClose={() => {
                    // Desmonta o painel antes de devolver o foco: com ele montado, o foco preso no modal
                    // puxaria de volta, e o foco acabaria no <body>.
                    flushSync(() => setOpen(false));
                    pendingFile.current = null;
                    opener.current?.focus();
                  }}
                />
              </PendingPhotoContext.Provider>
            </Suspense>
          )}
        </StateContext.Provider>
      </EntryContext.Provider>
    </OpenContext.Provider>
  );
}

/** Botão "Assistente" do shell; some quando o assistente não está liberado para o usuário. */
export function AssistantButton({ compact = false }: { compact?: boolean }) {
  const open = useContext(OpenContext);
  if (!open) return null;
  return compact ? (
    <Button variant="ghost" size="icon" aria-label="Assistente" onClick={open}>
      <Sparkles aria-hidden="true" className="size-5" />
    </Button>
  ) : (
    <Button variant="secondary" onClick={open}>
      <Sparkles aria-hidden="true" className="size-[18px]" />
      Assistente
    </Button>
  );
}
